/**
 * Task 128 D2 — useWorkblocksRange
 *
 * Returns the per-day work/break summary for a single employee across the
 * supplied date range. Mirrors the dashboard's existing data-source hook
 * pattern (useEmployeeDetail.ts): `useQuery` + `apiFetch`, gated by
 * `!mockupMode && !!orgId && !!employeeId`.
 *
 * Mockup mode synthesises the same response shape from the fixture
 * `TIMESHEETS[employeeId].dailyRows` so the page's internal plumbing
 * flows through one type regardless of mode.
 */
import { useQuery } from '@tanstack/react-query';
import { useAppContext } from '@/context/AppContext';
import { useToken } from '@/lib/useToken';
import { apiFetch } from '@/lib/api';
import { TIMESHEETS } from '@/data/timesheets';
import type { WorkBlocksRangeResponse } from '@/types/dashboard';

interface UseWorkblocksRangeResult {
  data: WorkBlocksRangeResponse | null;
  isLoading: boolean;
  error: Error | null;
}

/** Synthesize a WorkBlocksRangeResponse from the mockup fixture dailyRows. */
function synthFromFixture(
  employeeId: string,
  startDate: string,
  endDate: string,
): WorkBlocksRangeResponse | null {
  const bundle = TIMESHEETS[employeeId];
  if (!bundle) return null;

  // Half-open range [start, end) to match backend semantics.
  const inRange = (d: string) => d >= startDate && d < endDate;
  const rows = bundle.dailyRows.filter((r) => inRange(r.date));

  const days = rows.map((r) => {
    const totalMin = Math.round(r.totalHours * 60);
    // Rough break approximation for mockup: full = ~60m, half = ~20m, checkin = 0.
    const breakMin = r.dayType === 'full' ? 60 : r.dayType === 'half' ? 20 : 0;
    // Event count — approximate against the dashboard's minutesPerScreenshot=1
    // mockup default; value is only used by the page to detect empty days.
    const eventCount = totalMin;
    const sessions = r.dayType === 'full' ? 3 : r.dayType === 'half' ? 2 : 1;
    return {
      date: r.date,
      totalWorkMinutes: totalMin,
      totalBreakMinutes: breakMin,
      sessions,
      eventCount,
      firstEvent: null,
      lastEvent: null,
    };
  });

  const totalWorkMinutes = days.reduce((s, d) => s + d.totalWorkMinutes, 0);
  const daysWithActivity = days.filter((d) => d.eventCount > 0).length;
  return {
    days,
    summary: {
      totalWorkMinutes,
      daysWithActivity,
      avgWorkMinutesPerDay: daysWithActivity > 0 ? totalWorkMinutes / daysWithActivity : 0,
    },
  };
}

export function useWorkblocksRange(
  employeeId: string | null,
  startDate: string,
  endDate: string,
): UseWorkblocksRangeResult {
  const { mockupMode, currentOrg } = useAppContext();
  const getToken = useToken();
  const orgId = currentOrg.id;

  const query = useQuery<WorkBlocksRangeResponse>({
    queryKey: ['workblocksRange', orgId, employeeId, startDate, endDate],
    queryFn: async () => {
      const token = await getToken();
      const qs = new URLSearchParams();
      if (startDate) qs.set('startDate', startDate);
      if (endDate) qs.set('endDate', endDate);
      return apiFetch<WorkBlocksRangeResponse>(
        `/api/v1/orgs/${orgId}/employees/${employeeId}/workblocks-range?${qs.toString()}`,
        token,
      );
    },
    enabled: !mockupMode && !!orgId && !!employeeId && !!startDate && !!endDate,
  });

  if (mockupMode) {
    const data = employeeId ? synthFromFixture(employeeId, startDate, endDate) : null;
    return { data, isLoading: false, error: null };
  }

  if (!employeeId) {
    return { data: null, isLoading: false, error: null };
  }

  return {
    data: query.data ?? null,
    isLoading: query.isLoading,
    error: (query.error as Error | null) ?? null,
  };
}
